<template>
  <Teleport to="body">
    <div v-show="isOpen" class="subscribe-modal-overlay" @click.self="close">
      <div class="subscribe-modal-content">
        <div class="subscribe-modal-body">
          <div class="subscribe-modal-title">
            Subscribe to {{ newsletter.name }}
          </div>
          <SubscribeFormContainer
            v-if="isOpen"
            :newsletter="newsletter"
            :subscriber-facing-authentication="subscriberFacingAuthentication"
            :on-subscribe="handleSubscribe"
            variant="subscribe_page"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref } from "vue";

import type { Newsletter } from "@/types/newsletter";

import type { SubscriberFacingAuthentication } from "./SubscribeForm/lib";
import SubscribeFormContainer from "./SubscribeForm/SubscribeFormContainer.vue";

defineProps<{
  newsletter: Newsletter;
  subscriberFacingAuthentication: SubscriberFacingAuthentication;
}>();

const isOpen = ref(false);

const open = () => {
  isOpen.value = true;
  document.body.style.overflow = "hidden";
  nextTick(() => {
    const emailInput = document.querySelector<HTMLInputElement>(
      ".subscribe-modal-body #sfi-email"
    );
    emailInput?.focus();
  });
};

const close = () => {
  isOpen.value = false;
  document.body.style.overflow = "";
};

const handleSubscribe = () => {
  // Keep the modal open so the user sees the success state
};

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === "Escape" && isOpen.value) {
    close();
  }
};

const handleClick = (e: MouseEvent) => {
  const target = e.target as HTMLElement;
  if (!target.closest(".subscribe-modal-trigger")) {
    return;
  }

  // The trigger is an anchor with an href fallback (so it still works without
  // JS); suppress that navigation now that we're opening the modal instead.
  e.preventDefault();

  // Close the mobile menu if it's open
  const toggle = document.querySelector(".header-navigation-toggle");
  const nav = document.querySelector(".header-navigation-nav");
  const overlay = document.querySelector(".header-navigation-overlay");
  if (
    toggle &&
    nav &&
    overlay &&
    toggle.getAttribute("aria-expanded") === "true"
  ) {
    toggle.setAttribute("aria-expanded", "false");
    nav.setAttribute("aria-expanded", "false");
    overlay.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
  }

  open();
};

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
  document.addEventListener("click", handleClick);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  document.removeEventListener("click", handleClick);
});
</script>

<style scoped>
.subscribe-modal-overlay {
  position: fixed;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(1px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  animation: fadeIn 0.25s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.subscribe-modal-content {
  background-color: var(--color-background-card, #ffffff);
  border-radius: 8px;
  border: 1px solid var(--color-background-offset, #e5e7eb);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 480px;
  max-width: calc(100% - 2rem);
  animation: slideUp 0.25s ease-out;
  overflow: hidden;
  font-family: var(--font-family, sans-serif);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media (max-width: 600px) {
  .subscribe-modal-overlay {
    align-items: flex-end;
    padding: 0;
  }

  .subscribe-modal-content {
    width: 100%;
    max-width: 100%;
    border-radius: 0;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    border-bottom: none;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1),
      0 -2px 4px -1px rgba(0, 0, 0, 0.06);
    animation: slideUpFromBottom 0.2s ease-out;
  }

  @keyframes slideUpFromBottom {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
}

.subscribe-modal-header {
  display: flex;
  justify-content: flex-end;
  padding: 12px 12px 0;
}

.subscribe-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: var(--color-foreground-offset, #6b7280);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s;
}

.subscribe-modal-close:hover {
  color: var(--color-foreground, #111827);
}

.subscribe-modal-body :deep(.card) {
  border: none;
  background: none;
  margin-bottom: var(--space);
  margin-top: var(--space);
  padding-top: 0;
  padding-bottom: 0;
}

.subscribe-modal-title {
  padding-left: var(--space);
  padding-right: var(--space);
  padding-top: var(--space);
  text-align: center;
  font-weight: bold;
}
</style>
